/**
 * Everything a bubble wears besides its body: the hover action bar above it and the
 * reaction row below it.
 *
 * ## Why they are one component
 *
 * They are drawn in different places and could have been two, but both halves run off the
 * same read: the reactions WITH the viewer's unanswered clicks applied, not
 * `message.reactions`, which is a round trip behind. The row paints its pills from it, the
 * bar's add button writes through the same `toggleReaction`, and the cluster's width floor
 * is decided by whether that read has anything in it. Two components would each need it,
 * and reading it twice means mounting `useChannelReactions` twice, which puts two add- and
 * two remove-mutation observers on every message in the thread.
 *
 * Mounting it once here is also what keeps `ConversationMessageItem`'s memo load-bearing:
 * this component subscribes to the pending-intent store itself, so a reaction click
 * repaints the chrome and nothing else. Hoisting the hook into the item instead would
 * rebuild a `MarkdownIt` parser per bubble on every toggle. The item must never read
 * reaction state directly.
 *
 * ## What each half is for
 *
 * - The **action bar** is drawn for every message — it is what a hover reveals, and it is
 *   the only way to reach copy/edit/reply/more. It opens with the add-reaction button, and
 *   keeps it whether or not the message already has reactions, so the bar is one fixed set
 *   of controls in one fixed order. See `channel-message-action-bar`.
 * - The **reaction row** is drawn only once there is something in it, because it is in
 *   flow: an empty row would spend real height on every message in the thread. It hangs
 *   inside `BubbleCluster`, the box that hugs the bubble, which is what makes it the
 *   bubble's width. See `channel-message-reaction-footer`.
 *
 * Reactions are offered only while `canReactInChannel` holds — the type offers them and the
 * channel takes new messages. In a closed channel the pills stay visible but
 * read-only, because the server would answer 409. A thread with reactions off (support)
 * never reaches the body below, so it pays for nothing but the bar.
 */

import { cn } from "cn";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { BubbleBox, BubbleCluster } from "@/components/ui/conversation";
import type { Channel, ChannelMessage } from "../_api/channel-endpoints";
import { useChannelReactions } from "../_api/use-channel-reactions";
import {
  reapLandedReactionIntents,
  usePendingReactions,
} from "../_hooks/use-pending-reactions";
import {
  canReactInChannel,
  hasVisibleReactions,
  rankReactions,
} from "../_libs/message-reaction";
import { isOptimisticMessage } from "../_libs/optimistic-message";
import { applyPendingReactions } from "../_libs/pending-reactions";
import type { ChannelMessageActionGroups } from "../_types/channel-surface";
import { ChannelMessageActionBar } from "./channel-message-action-bar";
import {
  ChannelMessageReactionFooter,
  REACTION_ROW_MIN_WIDTH,
} from "./channel-message-reaction-footer";

export type ChannelMessageChromeProps = {
  cid: string;
  channel: Channel;
  message: ChannelMessage;
  viewerId: string | undefined;
  /**
   * Which side the bubble sits on. It picks the corner the bar is anchored to, the edge the
   * reaction row packs against, the tint its pills borrow, and the side the picker opens from.
   */
  align: "start" | "end";
  /**
   * The message's actions, already built AND already split between the bar's icons and its
   * "More" menu — `conversation/_libs/message-actions.ts` owns that policy.
   */
  actions: ChannelMessageActionGroups;
  /**
   * The bubble. It is wrapped rather than placed beside, because the action bar anchors to
   * the BUBBLE's edge and the bubble is the only thing in the column that is its width.
   * Passing it through costs nothing: it is the same element object on every chrome
   * re-render, so React bails out of re-rendering the bubble's subtree.
   */
  children: ReactNode;
  /** Worn by the box around the bubble — how wide the bubble is allowed to be. */
  bubbleBoxClassName?: string;
  /**
   * False on a surface that draws no chrome at all. The bubble still renders — this
   * component owns the box around it either way, so one definition of that box serves both.
   */
  showChrome?: boolean;
};

export function ChannelMessageChrome({
  cid,
  channel,
  message,
  viewerId,
  align,
  actions,
  children,
  bubbleBoxClassName,
  showChrome = true,
}: ChannelMessageChromeProps) {
  if (!showChrome) {
    return <BubbleBox className={bubbleBoxClassName}>{children}</BubbleBox>;
  }

  // The picker writes, so it is offered only where a write would be accepted — and never on
  // a message that is not on the server yet, which has no id to react to.
  const canReact = canReactInChannel(channel) && !isOptimisticMessage(message);

  // The MOUNT gate reads the server's reactions, not the viewer's pending ones, and that is
  // sound: an intent can only exist on a message where `canReact` held at click time, so
  // this cannot be false while one is pending. A message that can neither take nor show a
  // reaction gets the bar alone, and mounts none of the reaction machinery.
  if (!canReact && !hasVisibleReactions(channel, message)) {
    return (
      <BubbleWithActions
        className={bubbleBoxClassName}
        align={align}
        actions={actions}
      >
        {children}
      </BubbleWithActions>
    );
  }

  return (
    <ChannelMessageChromeBody
      cid={cid}
      channel={channel}
      message={message}
      viewerId={viewerId}
      align={align}
      actions={actions}
      canReact={canReact}
      bubbleBoxClassName={bubbleBoxClassName}
    >
      {children}
    </ChannelMessageChromeBody>
  );
}

/**
 * The chrome once the gate above has decided the message is in the reactions business.
 *
 * Split from that gate so `useChannelReactions` and the pending-intent subscription are
 * mounted ONCE per bubble, and only on a bubble that can show or take a reaction — the gate
 * returns before this component exists.
 *
 * What the row draws is the cache WITH the viewer's unanswered clicks applied, so a stale
 * answer or a refetch landing mid-burst cannot un-draw one — see `_hooks/use-pending-reactions`.
 * Every reader below works off that, `hasPills` included: on the server's reactions alone,
 * the FIRST reaction on a message would not paint until a round trip after the click that
 * placed it.
 *
 * This is also the one place that can retire a click, because it is the only one that knows
 * what was actually drawn. The effect below hands the rendered reactions back to the store,
 * which forgets every settled intent they have caught up with — and not a moment before, so
 * the fallback to `message.reactions` is only ever taken once `message.reactions` provably
 * agrees.
 */
function ChannelMessageChromeBody({
  cid,
  channel,
  message,
  viewerId,
  align,
  actions,
  children,
  bubbleBoxClassName,
  canReact,
}: ChannelMessageChromeProps & { canReact: boolean }) {
  const { toggleReaction } = useChannelReactions(cid, viewerId);

  const intents = usePendingReactions(cid, message.id);
  const reactions = viewerId
    ? applyPendingReactions(message.reactions, intents, viewerId)
    : message.reactions;

  const hasPills = hasVisibleReactions(channel, { id: message.id, reactions });

  // After the commit, never during it: what retires an intent is the reactions this bubble
  // HAS drawn, so the read has to happen where that is already true. The store returns
  // without notifying when nothing landed, so the common render costs one comparison and no
  // re-render.
  useEffect(() => {
    if (!viewerId) return;
    reapLandedReactionIntents(cid, message.id, message.reactions, viewerId);
  }, [cid, message.id, message.reactions, viewerId]);

  return (
    // The cluster is what the row stretches to: it hugs the bubble, so the row is the
    // bubble's width, and it carries the floor that keeps a two-character message's
    // reactions readable. The floor rides HERE and not on the box, because the box is what
    // the action bar anchors to — see `components/ui/conversation/bubble.tsx`.
    //
    // `bubbleBoxClassName` is worn by both: it is how a caller says "this bubble takes the
    // whole column" (a voice note), and a `w-full` on the cluster alone would leave the box
    // inside it still hugging. Each resolves against the box outside it, so the cluster
    // takes the column's width and the box then takes the cluster's.
    <BubbleCluster
      className={cn(bubbleBoxClassName, hasPills && REACTION_ROW_MIN_WIDTH)}
    >
      {/* `self-end` and not the cluster's `items-end`: aligning every child would take the
          row's stretch away with it, and a row sized by its own contents under
          `contain: inline-size` is 0 wide. */}
      <BubbleWithActions
        className={cn(bubbleBoxClassName, align === "end" && "self-end")}
        align={align}
        actions={actions}
        // Offered for as long as a reaction would be accepted, pills or no pills: the bar
        // is one fixed set of controls, and a button that disappears once the message has
        // a reaction is a control the reader has to re-find.
        onPickReaction={
          canReact ? (type) => toggleReaction(message, type) : undefined
        }
      >
        {children}
      </BubbleWithActions>

      {hasPills && (
        <ChannelMessageReactionFooter
          channel={channel}
          message={message}
          viewerId={viewerId}
          align={align}
          reactions={rankReactions(reactions, viewerId)}
          canReact={canReact}
          toggleReaction={toggleReaction}
        />
      )}
    </BubbleCluster>
  );
}

/**
 * The bubble's box with the action bar over it, and the one piece of state that pair needs.
 *
 * A control that finishes something retires the bar until the pointer leaves the message —
 * see `channel-message-action-bar` for why. The state lives HERE rather than in the bar
 * because only this level can read the reset: the bar is absolutely positioned ABOVE the
 * bubble, so its own `pointerleave` fires the moment the pointer drops onto the box it is
 * still hovering. `BubbleBox`'s fires when the pointer actually leaves the message, which is
 * the same boundary `group-hover` reveals the bar on.
 *
 * `BubbleBox` is the hover `group`, so the bar has to stay inside it: both halves of this
 * pair are one decision, and splitting them would leave a caller to re-pair them correctly.
 */
function BubbleWithActions({
  className,
  align,
  actions,
  onPickReaction,
  children,
}: {
  className?: string;
  align: "start" | "end";
  actions: ChannelMessageActionGroups;
  onPickReaction?: (type: string) => void;
  children: ReactNode;
}) {
  // Setting it back to `false` on a leave it is already `false` on costs nothing — React
  // bails out of the re-render when the value has not changed.
  const [dismissed, setDismissed] = useState(false);

  return (
    <BubbleBox className={className} onPointerLeave={() => setDismissed(false)}>
      {children}
      <ChannelMessageActionBar
        align={align}
        actions={actions}
        onPickReaction={onPickReaction}
        dismissed={dismissed}
        onDismiss={() => setDismissed(true)}
      />
    </BubbleBox>
  );
}
